import React, { useEffect, useState, ReactNode } from 'react';

// Material ui
import { Grid } from '@material-ui/core';

// Components
import Button from '@components/UI-KIT/Button';
import Footer from '@components/UI-KIT/Footer';
import AmountFormat from '@components/utils/AmountFormat';
import Typography from '@components/UI-KIT/Typography';
import Divider from '@components/UI-KIT/Divider';
import DateFormat from '@components/utils/DateFormat';

// Types
import { TransactionDto, TransactionBetweenAccountsDto } from '@services/account-transaction';
import { SelectOption } from '@components/UI-KIT/Inputs/SelectInput';
import { Contract } from '@services/contract';

// Icons
import {
    LeftArrow,
    Income,
    Outgoing,
    MovementOutlined
} from '@/assets/icons';

// Styles
import VARIABLES from '@/assets/variables';

interface Props {
    transaction: TransactionDto;
    transactionType: string;
    transactionBetween: TransactionBetweenAccountsDto
    setDrawer: React.Dispatch<React.SetStateAction<{
        drawerOpened: boolean;
        drawerTitle: ReactNode | string;
        drawerChildren: ReactNode;
        drawerFooter: boolean;
        titleIcon: ReactNode;
    }>>;
    accounts: SelectOption[];
    concepts: SelectOption[];
    tenantSelectOptions: SelectOption[];
    propietorSelectOptions: SelectOption[];
    contracts: Contract[];
    setActiveStep: React.Dispatch<React.SetStateAction<number>>;
    handlePostTransaction: () => Promise<void>;
    postStatus: string;
};

const ThirdStep: React.FC<Props> = ({
    transaction,
    transactionType,
    setActiveStep,
    accounts,
    transactionBetween,
    concepts,
    setDrawer,
    contracts,
    handlePostTransaction,
    tenantSelectOptions,
    postStatus,
    propietorSelectOptions
}) => {
    const [ dataToShow, setDataToShow ] = useState({
        account: '',
        concept: '',
        property: null,
        person: null,
        associatedTo: '',
        contract: null,
        period: '',
        description: '',
        date: ''
    });
    const [ dataToShowMov, setDataToShowMov ] = useState({
        rootAccount: '',
        destinationAccount: '',
        date: '',
        description: ''
    });

    useEffect(() => {
        const contract = contracts.find((currentContract: Contract) => transaction.contract === currentContract.id);
        const  person  = transaction.associatedTo === 'Tenant'
            ? tenantSelectOptions.find(({ value }: SelectOption) => value.id === transaction.associatedTenant).value.person
            : transaction.associatedTo === 'Propietor'
                ? propietorSelectOptions.find(({ value }: SelectOption) => value.id === transaction.associatedPropietor).value.person
                : null;

        setDrawer(prevState => ({
            ...prevState,
            drawerTitle: 'Revisá que esté todo OK',
            titleIcon: transactionType === 'Income'
                ? <Income />
                : transactionType === 'Outgoing'
                    ? <Outgoing />
                    : <MovementOutlined />
        }));
        if(transactionType !== 'Movement'){
            setDataToShow({
                account: accounts.find(account => account.value.id === transaction.account).label,
                concept: concepts.find(concept => concept.value.id === transaction.concept).label,
                property: contract
                && `NC ${contract.property.externalReference}`,
                person: person && `${person.firstname} ${person.lastname}`,
                associatedTo: transaction.associatedTo === 'Tenant'
                    ? 'Inquilino'
                    : transaction.associatedTo === 'Propietor'
                        ? 'Propietario'
                        : 'Administración',
                contract: contract
                && (() => {
                    const [
                        startYear,
                        startMonth,
                        _startDate
                    ] = contract.startDate.split('-');
                    const [
                        endYear,
                        endMonth,
                        _endDate
                    ] = contract.endDate.split('-');
                    return `${contract.tenants[0].person.lastname} - ${startMonth}/${startYear.slice(2)} a ${endMonth}/${endYear.slice(2)}`;
                })(),
                period: transaction.period && (() => {
                    const [
                        year,
                        month,
                        _date
                    ] = transaction.period.split('-');
                    return `${month}/${year.slice(2)}`;
                })(),
                description: transaction.description,
                date: transaction.date
            });
        }
        else{
            setDataToShowMov({
                rootAccount: accounts.find(rootAccount => rootAccount.value.id === transactionBetween.rootAccount).label,
                destinationAccount: accounts.find(destinationAccount => destinationAccount.value.id === transactionBetween.destinationAccount).label,
                description: transactionBetween.description,
                date: transactionBetween.date
            });
        }
    }, [ ]);

    return (
        <>
            <Grid
                container
                direction="column"
                spacing={1}
            >
                <Grid
                    item
                >
                    <Typography
                        color="primary-dark"
                    >
                        {`${transactionType === 'Income' ? 'In' : 'E'}gresarás un total de`}
                    </Typography>
                </Grid>
                <Grid
                    item
                >
                    <Typography
                        color="primary-dark"
                        variant="h1"
                    >
                        <AmountFormat
                            value={transactionType !== 'Movement' ? transaction.amount : transactionBetween.amount}
                        />
                    </Typography>
                </Grid>
                <Grid
                    item
                >
                    { transactionType !== 'Movement' ? (
                        <Typography
                            color="primary-dark"
                        >
                            {`${transactionType === 'Income' ? 'a' : 'de'} la cuenta `}
                            <span
                                className="AOL-transaction-form-stepper-dataToShow-item"
                            >
                                {dataToShow.account}
                            </span>
                            &nbsp;por el concepto de&nbsp;
                            <span
                                className="AOL-transaction-form-stepper-dataToShow-item"
                            >
                                {dataToShow.concept}
                            </span>
                        </Typography>
                    ) : (
                        <Typography
                            color="primary-dark"
                        >
                            de la cuenta
                            &thinsp;
                            <span
                                className="AOL-transaction-form-stepper-dataToShow-item"
                            >
                                {dataToShowMov.rootAccount}
                            </span>
                            &nbsp;a la cuenta &nbsp;
                            <span
                                className="AOL-transaction-form-stepper-dataToShow-item"
                            >
                                {dataToShowMov.destinationAccount}
                            </span>
                        </Typography>
                    )}
                </Grid>
                { transactionType !== 'Movement' && (
                    <Grid
                        item
                        container
                        direction="column"
                        spacing={2}
                    >
                        <Grid
                            item
                        >
                            <Divider />
                        </Grid>
                        <Grid
                            item
                            container
                            justify="space-between"
                        >
                            <Typography
                                color="primary-dark"
                                variant="input-head"
                            >
                                Asociado a
                            </Typography>
                            <Typography
                                color="primary-dark"
                            >
                                {dataToShow.associatedTo}
                            </Typography>
                        </Grid>
                        <Grid
                            item
                        >
                            <Divider />
                        </Grid>
                        { dataToShow.person && (
                            <>
                                <Grid
                                    item
                                    container
                                    justify="space-between"
                                >
                                    <Typography
                                        color="primary-dark"
                                        variant="input-head"
                                    >
                                        Nombre del&nbsp;
                                        {`${transaction.associatedTo === 'Tenant' ? 'inquilino' : 'propietario'}`}
                                    </Typography>
                                    <Typography
                                        color="primary-dark"
                                    >
                                        {dataToShow.person}
                                    </Typography>
                                </Grid>
                                <Grid
                                    item
                                >
                                    <Divider />
                                </Grid>
                            </>
                        )}
                        { transaction.includeBilling && (
                            <>
                                <Grid
                                    item
                                    container
                                    justify="space-between"
                                >
                                    <Typography
                                        color="primary-dark"
                                        variant="input-head"
                                    >
                                        Propiedad
                                    </Typography>
                                    <Typography
                                        color="primary-dark"
                                    >
                                        {dataToShow.property}
                                    </Typography>
                                </Grid>
                                <Grid
                                    item
                                >
                                    <Divider />
                                </Grid>
                                <Grid
                                    item
                                    container
                                    justify="space-between"
                                >
                                    <Typography
                                        color="primary-dark"
                                        variant="input-head"
                                    >
                                        Contrato
                                    </Typography>
                                    <Typography
                                        color="primary-dark"
                                    >
                                        {dataToShow.contract}
                                    </Typography>
                                </Grid>
                                <Grid
                                    item
                                >
                                    <Divider />
                                </Grid>
                                <Grid
                                    item
                                    container
                                    justify="space-between"
                                >
                                    <Typography
                                        color="primary-dark"
                                        variant="input-head"
                                    >
                                        Período
                                    </Typography>
                                    <Typography
                                        color="primary-dark"
                                    >
                                        {dataToShow.period}
                                    </Typography>
                                </Grid>
                                <Grid
                                    item
                                >
                                    <Divider />
                                </Grid>
                            </>
                        )}
                        <Grid
                            item
                            container
                            justify="space-between"
                        >
                            <Typography
                                color="primary-dark"
                                variant="input-head"
                            >
                                Descripción
                            </Typography>
                            <Typography
                                color="primary-dark"
                            >
                                {dataToShow.description || '-'}
                            </Typography>
                        </Grid>
                        <Grid
                            item
                        >
                            <Divider />
                        </Grid>
                        <Grid
                            item
                            container
                            justify="space-between"
                        >
                            <Typography
                                color="primary-dark"
                                variant="input-head"
                            >
                                Fecha
                            </Typography>
                            <Typography
                                color="primary-dark"
                            >
                                <DateFormat
                                    value={dataToShow.date}
                                />
                            </Typography>
                        </Grid>
                        {/* <Grid
                        item
                    >
                    	<Divider />
                    </Grid> */}
                    </Grid>
                )}
                { transactionType === 'Movement' && (
                    <>
                        <Grid
                            item
                            container
                            justify="space-between"
                        >
                            <Typography
                                color="primary-dark"
                                variant="input-head"
                            >
                                Descripción
                            </Typography>
                            <Typography
                                color="primary-dark"
                            >
                                {dataToShowMov.description || '-'}
                            </Typography>
                        </Grid>
                        <Grid
                            item
                        >
                            <Divider />
                        </Grid>
                        <Grid
                            item
                            container
                            justify="space-between"
                        >
                            <Typography
                                color="primary-dark"
                                variant="input-head"
                            >
                                Fecha
                            </Typography>
                            <Typography
                                color="primary-dark"
                            >
                                <DateFormat
                                    value={dataToShowMov.date}
                                />
                            </Typography>
                        </Grid>
                    </>
                )}
            </Grid>
            <Footer
                className="AOL-Account-stepper-footer"
            >
                <Grid
                    container
                    justify="space-between"
                >
                    <Button
                        label="Anterior"
                        disabled={postStatus === 'pending'}
                        level={3}
                        icon={(
                            <LeftArrow
                                color={VARIABLES.palette.secondary_color}
                            />
                        )}
                        onClick={() => setActiveStep(2)}
                    />
                    <Button
                        label="Confirmar"
                        disabled={postStatus === 'pending'}
                        onClick={handlePostTransaction}
                        loadingEvent
                    />
                </Grid>
            </Footer>
        </>
    );
};

export default ThirdStep;